import React from 'react';
import { ArrowRightIcon, CameraIcon, FileIcon, FileTextIcon, FolderIcon, VideoIcon, Volume2Icon } from 'lucide-react';
import { usePortal } from '../contexts/PortalContext';
import { StatusTag } from './StatusTag';
import { CLIENT_STAGE_ORDER, deliverableStage, metaLabel, stageHeading } from '../utils/deliverables';
import type { Deliverable } from '../types/portal';
function isOpenable(item: Deliverable): boolean {
  /* A transcript always opens: the page itself reads the stored transcript and
     says plainly whether one has been added yet, rather than depending on what
     this browser happens to hold. */
  if (item.type === 'Transcript') return true;
  const stage = deliverableStage(item.status);
  return stage === 'Review' || stage === 'Final Delivery';
}

/**
 * A quiet mark for the kind of work, not a stand-in for real footage — the
 * row never claims to show a frame that doesn't exist. Same ink-and-light
 * language as Sign In, so the deliverables list reads as a catalog of real
 * media rather than a plain task list. Hidden below sm: at that width it
 * would crowd out the title, which matters more.
 */
function TypeMark({
  type
}: {
  type: Deliverable['type'];
}) {
  const Icon = type === 'Video' ? VideoIcon : type === 'Audio' ? Volume2Icon : type === 'Photography' ? CameraIcon : type === 'Transcript' ? FileTextIcon : type === 'Folder' ? FolderIcon : FileIcon;
  return <span aria-hidden className="relative hidden h-9 w-14 shrink-0 items-center justify-center overflow-hidden bg-ink sm:flex" data-id="element-73" data-mp="2gc7bl2811jsw">
      <span className="absolute inset-0" style={{
      background: 'radial-gradient(120% 140% at 20% 0%, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0.05) 45%, rgba(255,255,255,0) 75%)'
    }} data-id="element-74" data-mp="26l4dm9ae9kxu" />
      <Icon className="relative h-3.5 w-3.5 text-paper/70" strokeWidth={1.25} data-id="element-75" data-mp="2z8qqy99fc3xw" />
    </span>;
}

/**
 * Every deliverable Portal has marked visible, grouped by the stage it is in —
 * planned work included, without actions it does not yet have.
 */
export function DeliverableGroups() {
  const {
    clientDeliverables,
    openDeliverable
  } = usePortal();
  if (clientDeliverables.length === 0) {
    return <div className="border-t border-rule py-14" data-id="element-76" data-mp="3de8y8xakwst4">
        <span className="label block text-mute" data-id="element-77" data-mp="2zfixn7ojwb00">In Motion</span>
        <p className="mt-5 max-w-sm font-display text-2xl leading-snug text-graphite" data-id="element-78" data-mp="2zdy67qxj986d">
          Deliverables are being confirmed.
        </p>
        <p className="label-sm mt-4 text-mute" data-id="element-79" data-mp="2qf4as3vxunue">
          They&rsquo;ll appear here the moment Portal adds them.
        </p>
      </div>;
  }
  const groups = CLIENT_STAGE_ORDER.map(stage => ({
    stage,
    items: clientDeliverables.filter(item => deliverableStage(item.status) === stage)
  })).filter(group => group.items.length > 0);
  return <div data-id="element-80" data-mp="soa35oemue7x">
      {groups.map((group, groupIndex) => <section key={group.stage} className={groupIndex === 0 ? '' : 'mt-12'} data-id="element-81" data-mp="2d6lvx2pucnzd" data-mpi={groupIndex}>
          <h3 className="label border-b border-rule pb-3 text-mute" data-id="element-82" data-mp="295sb4i7p3pap">
            {stageHeading(group.stage)}
          </h3>
          <ul className="m-0 list-none p-0" data-id="element-83" data-mp="2mx6tcmts3mgp">
            {group.items.map((item, index) => {
          const openable = isOpenable(item);
          return <li key={item.id} className="border-b border-rule" data-id="element-84" data-mp="2wv31r6vc03b4" data-mpi={index}>
                  <button type="button" disabled={!openable} onClick={() => openDeliverable(item.id)} className={['group grid w-full grid-cols-[3.5rem_1fr] items-baseline gap-x-5 gap-y-4 py-7 text-left sm:grid-cols-[3.25rem_4.5rem_1fr_auto] sm:gap-x-6', openable ? 'cursor-pointer' : 'cursor-default'].join(' ')} data-id="element-85" data-mp="2wt9x1x4a3t5r">
                    <TypeMark type={item.type} data-id="element-86" data-mp="16jyrctc066mw" />
                    <span className="font-display text-3xl leading-none text-mute sm:text-4xl" data-id="element-87" data-mp="18xc3lr3nd30x">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <span className="min-w-0" data-id="element-88" data-mp="1d0ryoj02ivbi">
                      <span className={['block font-display text-2xl leading-tight transition-colors duration-150 ease-out sm:text-4xl', openable ? 'text-ink group-hover:text-graphite' : 'text-graphite'].join(' ')} data-id="element-89" data-mp="1hxqnqn4vkc2u">
                        {item.name}
                      </span>
                      <span className="label-sm mt-3 block text-mute" data-id="element-90" data-mp="z6hf0g2xhrcb">{metaLabel(item)}</span>
                    </span>
                    <span className="col-start-2 flex items-center gap-4 sm:col-start-4" data-id="element-91" data-mp="13ztvg2xgertz">
                      <StatusTag status={item.status} data-id="element-92" data-mp="6ibvfwnm28e2" />
                      {openable && <ArrowRightIcon className="hidden h-3.5 w-3.5 text-mute transition-transform duration-150 ease-out group-hover:translate-x-1 sm:block" strokeWidth={1.25} data-id="element-93" data-mp="bgn2y0qfo6t4" />}
                    </span>
                  </button>
                </li>;
        })}
          </ul>
        </section>)}
    </div>;
}